// super-excalidraw: client-side helper that renders every Frame element
// in the current scene to a PNG and PUTs it to the server's frame cache.
// Triggered after each successful autosave so that AI agents (via MCP)
// can read fresh, frame-by-frame snapshots without running a browser.

import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

const exportModulePromise = import("@excalidraw/utils/export");

export type FrameRenderDTO = {
  id: string;
  name?: string;
  index: number;
  width?: number;
  height?: number;
  mimeType?: string;
  contentLength?: number;
  updatedAt: number;
};

export type FrameRenderListDTO = {
  items: FrameRenderDTO[];
  updatedAt?: number;
};

export const listFrameRenders = async (
  diagramId: string,
): Promise<FrameRenderListDTO> => {
  const r = await fetch(
    `/api/diagrams/${encodeURIComponent(diagramId)}/frames/`,
  );
  if (!r.ok) {
    throw new Error(`frames list ${r.status}`);
  }
  return r.json();
};

const blobToBuffer = async (b: Blob): Promise<ArrayBuffer> => b.arrayBuffer();

export const uploadFrameRenders = async (opts: {
  diagramId: string;
  excalidrawAPI: ExcalidrawImperativeAPI;
}): Promise<{ uploaded: number; pruned: number }> => {
  const { diagramId, excalidrawAPI } = opts;
  const elements = excalidrawAPI.getSceneElements() as readonly any[];
  const files = excalidrawAPI.getFiles();
  const appState = excalidrawAPI.getAppState();

  const liveFrames = elements.filter(
    (e) => e.type === "frame" && !e.isDeleted,
  );
  if (liveFrames.length === 0) {
    // Nothing to render — but we still want to prune any leftover entries
    // from a previous version of the diagram so the agent never sees stale
    // slides. Fire-and-forget; failure is non-fatal.
    fetch(`/api/diagrams/${encodeURIComponent(diagramId)}/frames/`, {
      method: "DELETE",
    }).catch(() => {});
    return { uploaded: 0, pruned: 0 };
  }

  const order =
    ((appState as any).customData?.presentationOrder as string[] | undefined) ??
    liveFrames.map((f) => f.id);

  const { exportToBlob } = await exportModulePromise;

  let uploaded = 0;
  await Promise.all(
    liveFrames.map(async (frame) => {
      // Render the frame plus only the elements that belong to it. If a
      // frame is empty we still render it (so the agent sees the empty
      // slide), but with a 1×1 fallback we skip the export to avoid noise.
      const children = elements.filter((e) => e.frameId === frame.id);
      try {
        const blob = await exportToBlob({
          elements: [...children, frame],
          appState: { ...appState, exportBackground: true, exportScale: 2 },
          files,
          mimeType: "image/png",
          maxWidthOrHeight: 3840,
          exportPadding: 8,
        } as any);
        if (!blob) {
          return;
        }
        const buffer = await blobToBuffer(blob);
        const idx = order.indexOf(frame.id);
        const params = new URLSearchParams({
          name: frame.name ?? "",
          index: String(idx >= 0 ? idx : 0),
          width: String(Math.round(frame.width || 0)),
          height: String(Math.round(frame.height || 0)),
        });
        const r = await fetch(
          `/api/diagrams/${encodeURIComponent(diagramId)}/frames/${encodeURIComponent(frame.id)}?${params}`,
          {
            method: "PUT",
            headers: { "Content-Type": "image/png" },
            body: buffer,
          },
        );
        if (r.ok) {
          uploaded++;
        }
      } catch (e) {
        console.warn("super-excalidraw: frame render failed", frame.id, e);
      }
    }),
  );

  // Prune entries the server still has but our scene no longer contains.
  let pruned = 0;
  try {
    const list = await listFrameRenders(diagramId);
    const liveIds = new Set(liveFrames.map((f) => f.id));
    const stale = (list.items || []).filter((it) => !liveIds.has(it.id));
    await Promise.all(
      stale.map(async (it) => {
        const r = await fetch(
          `/api/diagrams/${encodeURIComponent(diagramId)}/frames/${encodeURIComponent(it.id)}`,
          { method: "DELETE" },
        );
        if (r.ok) {
          pruned++;
        }
      }),
    );
  } catch (e) {
    // best-effort
  }
  return { uploaded, pruned };
};
